import { es } from 'date-fns/locale';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { PiCurrencyDollarBold } from 'react-icons/pi';
import { Button, Modal } from 'rizzui';
import { Vehicle } from '../../type/entities/vehicle-type';
import { DatePicker } from './DatePicker';

// Estilos CSS para asegurar visibilidad de elementos HeadlessUI
const customStyles = `
  [data-headlessui-state="open"] {
    overflow: visible !important;
  }
`;

interface SellVehicleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (saleDate: Date) => void;
    vehicle?: Vehicle | null;
    isLoading?: boolean;
    modalSize?: 'sm' | 'md' | 'lg' | 'full';
    cancelBtnText?: string;
    confirmBtnText?: string;
}

const SellVehicleModal: React.FC<SellVehicleModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    vehicle,
    isLoading = false,
    modalSize = 'md',
    cancelBtnText = 'Cancelar',
    confirmBtnText = 'Confirmar Venta',
}) => {
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [dateError, setDateError] = useState<string>('');

    // ---------------------------------------------------
    // MODAL ACTIONS
    // ---------------------------------------------------
    const handleConfirm = () => {
        if (!selectedDate) {
            setDateError('Debe seleccionar una fecha de venta');
            return;
        }

        setDateError('');
        onConfirm(selectedDate);
    };

    const handleCancel = () => {
        setSelectedDate(new Date());
        setDateError('');
        onClose();
    };

    const handleDateChange = (date: Date) => {
        setSelectedDate(date);
        if (dateError) {
            setDateError('');
        }
    };

    // ---------------------------------------------------
    // EFFECTS
    // ---------------------------------------------------
    useEffect(() => {
        const today = new Date();
        const purchaseDate = vehicle?.purchaseDate?.date ? new Date(vehicle.purchaseDate.date) : null;

        if (purchaseDate && purchaseDate > today) setSelectedDate(purchaseDate);
    }, [vehicle]);

    return (
        <>
            <style>{customStyles}</style>
            <Modal isOpen={isOpen && vehicle !== null} onClose={handleCancel} size={modalSize} className='overflow-visible'>
                <div className='flex flex-col justify-center p-2 pt-4'>
                    <div className='flex items-center justify-center gap-2 mb-4'>
                        <PiCurrencyDollarBold className="text-2xl text-green-600" />
                        <h4 className="text-2xl text-center font-semibold">
                            Vender Vehículo
                        </h4>
                    </div>
                    <p className="text-center text-gray-600 mb-4">
                        ¿Estás seguro de que deseas marcar el vehículo <strong>{vehicle?.plateNumber || ''}</strong> como vendido?
                    </p>
                </div>

                <div className='flex flex-col gap-4 my-4 px-4'>
                    <div className='flex flex-col gap-2'>
                        <label className="text-sm font-medium text-gray-700">
                            Fecha de Venta <span className='text-red-500'>*</span>
                        </label>
                        <div className="w-full" style={{ display: 'block', visibility: 'visible' }}>
                            <DatePicker
                                monthsShown={1}
                                placeholderText="Selecciona una fecha"
                                inputProps={{
                                    inputClassName: 'h-9 [&_input]:text-ellipsis bg-white',
                                }}
                                selected={selectedDate ? moment(selectedDate).toDate() : null}
                                onChange={(date) => handleDateChange(date as Date)}
                                minDate={vehicle?.purchaseDate ? new Date(vehicle.purchaseDate?.date) : ''}
                                locale={es}
                            />
                        </div>
                        {dateError && (
                            <span className="text-sm text-red-500">{dateError}</span>
                        )}
                    </div>

                    <div className='bg-yellow-50 border border-yellow-200 rounded-md p-3'>
                        <p className="text-sm text-yellow-800">
                            <strong>Nota:</strong> Una vez marcado como vendido, el vehículo no podrá ser editado.
                        </p>
                    </div>
                </div>

                <div className="flex justify-center p-4 gap-4">
                    <Button
                        onClick={handleConfirm}
                        disabled={isLoading}
                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                        isLoading={isLoading}
                    >
                        {confirmBtnText}
                    </Button>
                    <Button
                        onClick={handleCancel}
                        disabled={isLoading}
                        variant="outline"
                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                    >
                        {cancelBtnText}
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default SellVehicleModal;